import z from 'zod'

import { normalizePlacementPlace } from '../../util/normalize/normalizePlacementPlace'
import { normalizeWeather } from '../../util/normalize/normalizeWeather'

// prettier-ignore
const compressiveStrengthEntrySchema = z.object({
  // 材齢は「7日」「28日」以外にも 2日 / 3日 など現場運用で任意の日数が発生し得るため、正の整数で扱う
  ['材齢']: z.number().int().positive().nullish().catch(x => null),
  ['試験日']: z.iso.date().nullish().catch(x => null),
  ['強度値']: z.array(z.number().nullish().catch(x => null)).nullish().catch(x => null),
  ['平均値']: z.number().nullish().catch(x => null),
  ['合否']: z.enum(['合', '否']).nullish().catch(x => null),
  ['養生方法']: z.string().nullish().catch(x => null),
})

// prettier-ignore
export const testResultSchema = z.object({
  ['打設番号']: z.number().nullish().catch(x => null),
  ['打設日']: z.iso.date().nullish().catch(x => null),
  // 打設箇所は document のグルーピングキーになる重要な値のため、OCR由来の表記揺れ（全角/半角・スペース）をここで正規化する。
  // transform は z.string() 直後に置き、外側を nullish() で包む。こうしないと（transform を最外層にすると）
  // object のキーが optional でなくなり TestResult 型で打設箇所が必須になってしまうため。
  ['打設箇所']: z.string().transform(normalizePlacementPlace).nullish().catch(x => null),
  ['CON数量_m3']: z.number().nullish().catch(x => null),
  ['先送りモルタル_m3']: z.number().nullish().catch(x => null),
  ['Fc_N_per_mm2']: z.number().nullish().catch(x => null),
  ['Fq_N_per_mm2']: z.number().nullish().catch(x => null),
  ['種類']: z.string().nullish().catch(x => null),
  ['呼び強度']: z.number().nullish().catch(x => null),
  ['スランプ_cm']: z.number().nullish().catch(x => null),
  ['骨材寸法_mm']: z.number().nullish().catch(x => null),
  ['セメント']: z.string().nullish().catch(x => null),
  ['プラント名']: z.string().nullish().catch(x => null),
  // 試験機関名は帳票（document）の検査会社名に由来し、1つの試験結果に複数 document が紐づくため配列で保持する。
  // mergeTestResults で各 document の検査会社名を結合・重複排除した結果が入る。
  ['試験機関名']: z.string().array().optional(),
  ['受入検査結果']: z.object({
    ['スランプ_cm']: z.number().nullish().catch(x => null),
    ['空気量_%']: z.number().nullish().catch(x => null),
    ['コンクリート温度_℃']: z.number().nullish().catch(x => null),
    ['塩化物量_Kg_per_m3']: z.number().nullish().catch(x => null),
    ['単位水量_Kg_per_m3']: z.number().nullish().catch(x => null),
    ['スランプフロー']: z.string().nullish().catch(x => null),
    ['外気温_℃']: z.number().nullish().catch(x => null),
    ['単位容積質量']: z.number().nullish().catch(x => null), // 2026/02/17 現状帳票種別に追加している帳票にはこの項目は存在しないが、今後追加されるとのこと
  }).array().nullish().catch(x => null),
  ['圧縮強度試験']: z.object({
    ['構造体']: compressiveStrengthEntrySchema.array().nullish().catch(x => null),
    ['調合管理']: compressiveStrengthEntrySchema.array().nullish().catch(x => null),
    }).nullish().catch(x => null),
  // 2026/05/26 さらに追加
  // 打設日の天気。いったんenumにせず自由文字列。「晴れ/晴」のような送り仮名の揺れは帳票側に実在するため、
  // 短形へ統一して保存・比較する（normalizeWeather のコメント参照。transform の置き方は打設箇所と同じ理由）
  ['天気']: z.string().transform(normalizeWeather).nullish().catch(x => null),
  // 気温は専用フィールドを持たない。受入検査結果の「外気温」平均を Excel 出力時に算出して使う（Notion 指示による決定）。
  ['使用測定器']: z.string().nullish().catch(x => null),
  // 試験機関名は上部で string[] として定義済み（帳票右上のOCR抽出値を優先し、無ければ検査会社名メタデータでフォールバック）。
  ['判定基準強度']: z.number().nullish().catch(x => null),
})
export type TestResult = z.infer<typeof testResultSchema>
